import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  { name: 'Rahul Sharma',   company: 'Industrial Solutions Pvt. Ltd.',  stars: 5, role: 'Supply Chain Head',    text: 'PKRouteX has completely transformed our supply chain operations. Real-time GPS tracking, premium vehicles, and a dedicated account manager — second to none in the industry.' },
  { name: 'Anita Desai',    company: 'Fresh Agri Exports',              stars: 5, role: 'Operations Director',  text: 'Reliable, fast, and professional. Their 19ft trucks are perfect for our fruit distribution network across South India. Zero damage in 18 months of partnership.' },
  { name: 'Vikram Singh',   company: 'BuildCorp India',                 stars: 5, role: 'Logistics Manager',    text: 'Never had a single delay in 2 years. Best logistics partner for construction materials, period. The WhatsApp tracking updates are incredibly convenient.' },
  { name: 'Priya Nair',     company: 'QuickMart E-Commerce',            stars: 5, role: 'Founder & CEO',        text: 'Our last-mile delivery SLA improved by 40% after switching to PKRouteX. The digital proof of delivery feature alone saved us hours of manual work every week.' },
  { name: 'Arjun Mehta',    company: 'SteelForge Ltd.',                 stars: 5, role: 'Plant Manager',        text: 'Eicher trucks handled our heavy steel loads flawlessly. Professional team, zero damage record, and always on time. Highly recommended for industrial logistics.' },
  { name: 'Deepa Krishnan', company: 'Sunrise FMCG Distributors',       stars: 5, role: 'Distribution Head',   text: 'The part-load service is a game changer for our business. We save 30% on logistics costs while getting the same premium service. PKRouteX is our go-to partner.' },
];

const Stars = ({ count }) => (
  <div className="flex gap-1 mb-3 sm:mb-4">
    {[...Array(5)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: i * 0.06, type: 'spring' }}
      >
        <Star size={13} className={i < count ? 'text-brand-orange fill-brand-orange' : 'text-black/10'} />
      </motion.div>
    ))}
  </div>
);

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => { setDir(1); setCurrent(p => (p + 1) % testimonials.length); }, 4500);
    return () => clearInterval(t);
  }, [paused]);

  const go = (next) => {
    setDir(next > current ? 1 : -1);
    setCurrent((next + testimonials.length) % testimonials.length);
  };

  // Show 1 on mobile, 2 on sm, 3 on md+
  const visible = [
    (current - 1 + testimonials.length) % testimonials.length,
    current,
    (current + 1) % testimonials.length,
  ];

  return (
    <section className="section-padding bg-white overflow-hidden relative">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
        className="absolute top-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] border border-brand-orange/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-4"
        >
          <div className="inline-flex items-center gap-2 bg-brand-orange/5 border border-brand-orange/20 rounded-full px-4 sm:px-5 py-2 mb-5 sm:mb-6">
            <Star size={12} className="text-brand-orange fill-brand-orange" />
            <span className="text-brand-orange font-black tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs uppercase">Client Reviews</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-brand-black tracking-tighter">
            TRUSTED BY <span className="text-brand-orange">LEADERS</span>
          </h2>
          <p className="text-brand-muted max-w-md mx-auto text-sm sm:text-base px-4">
            Over 500+ businesses across India trust PKRouteX for their critical logistics operations.
          </p>
        </motion.div>

        <div
          className="relative mt-10 sm:mt-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Mobile: single card; sm: 2 cards; md+: 3 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {visible.map((tIdx, pos) => {
              const t = testimonials[tIdx];
              const isCenter = pos === 1;
              // Hide first and last on mobile, hide first on sm
              const hiddenOnMobile = pos !== 1 ? 'hidden sm:block' : '';
              const hiddenOnSm = pos === 0 ? 'hidden md:block' : '';
              return (
                <motion.div
                  key={`${tIdx}-${pos}`}
                  initial={{ opacity: 0, x: dir * 80, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: isCenter ? 1 : 0.96 }}
                  exit={{ opacity: 0, x: -dir * 80 }}
                  transition={{ duration: 0.45, type: 'spring', stiffness: 100 }}
                  whileHover={{ y: -8, scale: isCenter ? 1.02 : 0.98 }}
                  className={`p-5 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl relative border transition-all duration-300 cursor-pointer ${hiddenOnMobile} ${hiddenOnSm} ${
                    isCenter
                      ? 'border-brand-orange/30 bg-gradient-to-br from-orange-50 to-white shadow-xl shadow-brand-orange/10'
                      : 'border-black/5 bg-brand-bg shadow-sm'
                  }`}
                >
                  <motion.div
                    animate={{ rotate: [0, 5, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute top-4 sm:top-5 right-4 sm:right-6"
                  >
                    <Quote className="text-brand-orange/10" size={40} />
                  </motion.div>

                  <Stars count={t.stars} />
                  <p className="text-sm sm:text-base italic mb-4 sm:mb-6 text-brand-muted relative z-10 leading-relaxed">"{t.text}"</p>

                  <div className="flex items-center gap-3 pt-3 sm:pt-4 border-t border-black/5">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-10 sm:w-11 h-10 sm:h-11 rounded-full bg-brand-orange/15 border-2 border-brand-orange/30 flex items-center justify-center font-black text-brand-orange text-sm shrink-0"
                    >
                      {t.name.charAt(0)}
                    </motion.div>
                    <div className="min-w-0">
                      <h4 className="font-black text-brand-black text-sm truncate">{t.name}</h4>
                      <p className="text-brand-orange text-xs font-bold truncate">{t.role}</p>
                      <p className="text-slate-400 text-[10px] truncate">{t.company}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-10">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => go(current - 1)}
              className="w-10 sm:w-11 h-10 sm:h-11 rounded-full border border-black/10 flex items-center justify-center hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5 transition-all"
            >
              <ChevronLeft size={18} />
            </motion.button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <motion.button
                  key={i}
                  onClick={() => go(i)}
                  whileHover={{ scale: 1.3 }}
                  className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 sm:w-7 h-2 sm:h-2.5 bg-brand-orange' : 'w-2 sm:w-2.5 h-2 sm:h-2.5 bg-black/10 hover:bg-brand-orange/40'}`}
                />
              ))}
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => go(current + 1)}
              className="w-10 sm:w-11 h-10 sm:h-11 rounded-full border border-black/10 flex items-center justify-center hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5 transition-all"
            >
              <ChevronRight size={18} />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
